<script>
import { required, requiredIf } from 'vuelidate/lib/validators'
import api from '@/helpers/api'
import tableMixin from '@/mixins/table'

export default {
  name: 'remill.step',
  props: {
    value: { type: Object, default: () => {} }
  },
  mixins: [tableMixin],
  data: vm => ({
    isLoading: false,
    search: '',
    form: {
      not_found: false,
      specs: {
        last_order_date: null,
        last_reference: '',
        remill_webform_id: null,
        old_form_data: {}
      },
      selected_order: null,
      ...vm.value
    },
    fields: [
      {
        key: 'date',
        label: 'Date',
        thStyle: { width: '100px' }
      },
      {
        key: 'type',
        label: 'Item',
        thStyle: { width: '250px' }
      },
      {
        key: 'reference',
        label: 'Reference'
      }
    ]
  }),
  validations: {
    form: {
      specs: {
        last_reference: {
          required: requiredIf(model => model.not_found)
        }
      },
      selected_order: {
        required: requiredIf(model => !model.not_found)
      }
    }
  },
  watch: {
    search: 'onRefresh',
    currentPage: 'onRefresh',
    perPage: 'onRefresh',
    'form.specs.last_reference': {
      handler (val) {
        this.search = val
      },
      immediate: true
    }
  },
  methods: {
    validate () {
      if (!this.$v && !this.$v.form) return true
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        //set form_data from previous order if selected
        if (this.form.selected_order) {
          var keysToClone = 'foam_type'
          let { [keysToClone]: _, ...clonedSpecs } =
            this.form.selected_order.form_data

          this.form = {
            ...this.form,
            specs: {
              ...clonedSpecs,
              is_remill: true,
              last_order_date: this.form.selected_order.submitted_at,
              last_reference: this.form.selected_order.reference,
              //old_form_data: this.form.selected_order.form_data,
              remill_webform_id: this.form.selected_order.id,
              foam_type: this.form.selected_order.form_data.foam_type,
              last_foam_type: this.form.selected_order.form_data.foam_type,
              last_mix_seat_foam_type:
                this.form.selected_order.form_data.mix_seat_foam_type,
              last_mix_back_foam_type:
                this.form.selected_order.form_data.mix_back_foam_type
            }
          }
        } else {
          this.form = {
            ...this.form,
            specs: {
              ...this.form.specs,
              is_remill: true,
              last_order_date: this.form.last_order_date,
              last_reference: this.form.last_reference
            }
          }
        }

        this.$emit('validated', { ...this.form })
        this.$emit('input', { ...this.form })
      }

      return !this.$v.form.$invalid
    },
    dateDisabled (ymd, date) {
      // Disable weekends (Sunday = `0`, Saturday = `6`) and
      // disable days that fall on the 13th of the month
      //const weekday = date.getDay()

      const isPast = date < new Date()

      // Return `true` if the date should be disabled
      return !isPast
    },
    async onRefresh () {
      if (!this.search) {
        this.data = []
        this.form.selected_order = null
        return
      }
      this.isLoading = true
      let filters = [
        { is_submitted: { _eq: true } },
        { item_id: { _eq: this.form.item_id } }
        //{ form_data: { _contains: { model_type: this.form.specs.model_type } } }
      ]

      let { items, total_rows } = await api.webForms.fetchPage({
        page: this.currentPage,
        pageSize: this.perPage === 'All' ? -1 : this.perPage,
        filters,
        needle: this.search,
        orderBy: { submitted_at: 'desc' }
      })

      this.form.selected_order = null
      if (this.form.specs.remill_webform_id)
        this.form.selected_order = items.find(
          a => a.id === this.form.specs.remill_webform_id
        )
      this.data = items
      this.totalRows = total_rows
      this.isLoading = false
    },
    onRowClickedHandler (item, index, event) {
      this.form.selected_order = item
      this.form.specs.remill_webform_id = item.id
    },
    selectedRowClass (item, type) {
      if (!item || type !== 'row') return 'pointer'

      if (item.id === this.form.specs.remill_webform_id)
        return 'pointer table-primary'

      return 'pointer'
    }
  }
}
</script>
<template>
  <b-card no-body class="shadow-none">
    <b-row
      no-gutters
      align-h="end"
      align-v="center"
      class="mb-2"
      v-show="!form.not_found"
    >
      <g-search-input
        v-model="search"
        placeholder="Find Orders"
        width="300px"
        class="mr-0 ml-2"
      />
    </b-row>

    <div style="height: 250px; overflow-y: auto" v-show="!form.not_found">
      <b-table
        show-empty
        hover
        outlined
        :items="data"
        :fields="fields"
        :busy.sync="isLoading"
        head-variant="white"
        class="table-centered table-sm"
        :tbody-tr-class="selectedRowClass"
        stacked="md"
        :sticky-header="true"
        v-on:row-clicked="onRowClickedHandler"
      >
        <template v-slot:cell(date)="{ item }">
          {{ item.submitted_at | moment('DD/MM/YYYY') }}
        </template>
        <template v-slot:cell(type)="{ item }">
          <div class="d-flex justify-content-start align-items-center">
            <span class="text-truncate">{{ item.item.short_description }}</span>

            <template v-if="item.form_data.is_remill">
              <i
                :id="`ttRemill${item.id}`"
                class="mdi mdi-alpha-r-box text-primary font-size-16 ml-2"
              ></i>
              <b-tooltip
                :target="`ttRemill${item.id}`"
                variant="dark"
                placement="right"
              >
                {{ $t('Remill') }}
              </b-tooltip>
            </template>
            <template v-if="!!item.file">
              <i
                :id="`ttFile${item.file_id}${item.id}`"
                class="mdi mdi-file-check-outline text-primary font-size-16 ml-2"
              ></i>
              <b-tooltip
                :target="`ttFile${item.file_id}${item.id}`"
                variant="dark"
                title="File"
                placement="right"
              >
                <strong>
                  {{ item.file.file_name }}
                </strong>
                <small class="mx-2">{{
                  item.file.file_size | prettyBytes
                }}</small>
              </b-tooltip>
            </template>
          </div>

          <small v-if="item.form_data.hasOwnProperty('model_type')">
            {{ $t(`attribute.model_type.${item.form_data.model_type}`) }}
          </small>
        </template>

        <template v-slot:cell(reference)="{ item }">
          <span v-if="!!item.reference">{{ item.reference }}</span>
          <span v-else>{{ $t(`no reference`) }}</span>
          <br />
          <span
            class="small m-0 mr-1"
            v-if="item.form_data.hasOwnProperty('model_type')"
          >
            {{ $t(`attribute.model_type.${item.form_data.model_type}`) }}
          </span>
          -
          <span
            class="small m-0"
            v-if="item.form_data.hasOwnProperty('foam_type')"
            :id="`ttFoamType${item.id}`"
          >
            {{ $t(`attribute.foam_type.${item.form_data.foam_type}`) }}
          </span>

          <b-tooltip
            v-if="
              item.form_data.hasOwnProperty('foam_type') &&
              item.form_data.foam_type === 'mix'
            "
            :target="`ttFoamType${item.id}`"
            variant="dark"
            placement="right"
            custom-class="text-left"
            style="min-width: 300px"
          >
            <p class="mx-2 my-0">
              {{ $t('Seat') }}:
              {{ $t(`attribute.foam_type.${item.form_data.mix_seat_foam_type}`)
              }}<br />
              {{ $t('Back') }}:
              {{
                $t(`attribute.foam_type.${item.form_data.mix_back_foam_type}`)
              }}
            </p>
          </b-tooltip>
        </template>

        <template v-slot:cell(status)="{ item }">
          <app-status-badge :status="item.status" />
        </template>

        <template v-slot:cell(shipping)="{ item }">
          <p class="m-0 text-muted" v-if="item.shipping_address">
            <i class="fas fa-shipping-fast text-primary mr-1"></i>
            {{ item.shipping_address.company_name }},
            {{ item.shipping_address.city }} ({{
              item.shipping_address.country_code
            }})
          </p>
          <p v-else class="m-0 text-muted">{{ $t(`no shipping`) }}</p>
        </template>
      </b-table>
    </div>

    <b-row
      no-gutters
      class="my-2"
      align-h="end"
      align-v="center"
      v-show="!form.not_found"
    >
      <b-pagination
        v-if="perPage !== 'All' && totalRows > perPage"
        class="pagination mb-0 mt-2 justify-content-end"
        v-model="currentPage"
        :total-rows="totalRows"
        size="sm"
        :per-page="perPage"
      >
        <template v-slot:page="{ page, active }">
          <b v-if="active">{{ page }}</b>
          <i v-else>{{ page }}</i>
        </template>
      </b-pagination>
    </b-row>

    <b-row no-gutters class="my-2">
      <b-form-checkbox id="not_found" v-model="form.not_found" name="not_found">
        {{ $t('I cant find the order in the system') }}
      </b-form-checkbox>

      <div class="d-flex align-items-center justify-content-start m-0">
        <g-form-error-wrapper
          :validations="$v.form.specs.last_reference"
          class="p-0 m-0"
          v-if="form.not_found"
        >
          <template v-slot="{ errors, invalid }">
            <g-text-input
              v-model.number="form.specs.last_reference"
              :label="$t('Last Reference')"
              :error-messages="errors"
              :is-valid="!invalid"
              v-on:blur="$v.form.specs.last_reference.$touch()"
            >
            </g-text-input>
          </template>
        </g-form-error-wrapper>

        <g-date-input
          v-if="form.not_found"
          v-model="form.specs.last_order_date"
          :date-disabled-fn="dateDisabled"
          locale="en"
          class="m-0"
          :label="$t('Last Order Date')"
          :placeholder="$t('Date')"
        ></g-date-input>
      </div>
    </b-row>
  </b-card>
</template>
